import { useEffect } from 'react';
import axios from 'axios';

// Converts base64url VAPID public key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// True when running as installed PWA (standalone or fullscreen)
export function isPwa() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

export function usePushNotifications(token) {
  useEffect(() => {
    if (!token) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    // If user explicitly opted out, unsubscribe and stop
    const optedOut = localStorage.getItem('pushNotificationsEnabled') === 'false';
    if (optedOut) {
      navigator.serviceWorker.ready.then(async (reg) => {
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          await existing.unsubscribe();
          axios.delete('/api/push/unsubscribe', {
            data: { endpoint: existing.endpoint },
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }
      });
      return;
    }

    // Only auto-subscribe if not already denied by browser
    if (Notification.permission === 'denied') return;

    async function subscribe() {
      try {
        const { data } = await axios.get('/api/push/vapid-public-key');
        const publicKey = data.publicKey;
        if (!publicKey) return;

        const registration = await navigator.serviceWorker.ready;

        // Already subscribed — re-register with server (new session)
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          await axios.post('/api/push/subscribe', existing.toJSON(), {
            headers: { Authorization: `Bearer ${token}` },
          });
          return;
        }

        const storedPref = localStorage.getItem('pushNotificationsEnabled');

        // In PWA mode: auto-prompt if user hasn't explicitly opted out
        // In browser mode: only prompt if user explicitly opted in
        const shouldPrompt =
          storedPref === 'true' ||
          Notification.permission === 'granted' ||
          isPwa();

        if (!shouldPrompt) return;

        // Ask for permission
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        localStorage.setItem('pushNotificationsEnabled', 'true');

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        await axios.post('/api/push/subscribe', subscription.toJSON(), {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        console.warn('Push subscription failed:', err.message);
      }
    }

    // In PWA: prompt quickly (1.5s). In browser: wait longer (3s) to not disrupt UX
    const delay = isPwa() ? 1500 : 3000;
    const timer = setTimeout(subscribe, delay);
    return () => clearTimeout(timer);
  }, [token]);
}

// Exported helper to subscribe programmatically (called from settings)
export async function enablePushNotifications(token) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    throw new Error('Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.');
  }

  function urlBase64ToUint8ArrayLocal(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
  }

  const { data } = await axios.get('/api/push/vapid-public-key');
  const publicKey = data.publicKey;
  if (!publicKey) throw new Error('VAPID key nicht verfügbar.');

  const registration = await navigator.serviceWorker.ready;
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permission === 'denied' ? 'DENIED' : 'DISMISSED');
  }

  const existing = await registration.pushManager.getSubscription();
  const sub = existing || await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8ArrayLocal(publicKey),
  });

  await axios.post('/api/push/subscribe', sub.toJSON(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  localStorage.setItem('pushNotificationsEnabled', 'true');
}

export async function disablePushNotifications(token) {
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
      await axios.delete('/api/push/unsubscribe', {
        data: { endpoint: existing.endpoint },
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  }
  localStorage.setItem('pushNotificationsEnabled', 'false');
}
